import { Link } from 'react-router-dom';
import { articles, categories, stats } from '@/mocks/data';
import HeroSection from '@/pages/home/components/HeroSection';

export default function Home() {
  const featuredArticles = articles.filter((a) => a.featured).slice(0, 4);
  const latestArticles = articles.slice(0, 6);
  const cosplayArticles = articles.filter((a) => a.category === 'cosplay').slice(0, 3);
  const novelArticles = articles.filter((a) => a.category === 'light-novel').slice(0, 3);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '91次元',
    url: 'https://91acgn.com',
    description: '专为二次元爱好者打造的内容聚合平台，汇聚动漫、漫画、轻小说、Cosplay等多元次元文化。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://91acgn.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <title>91次元 - 二次元爱好者的次元聚集地 | 动漫番剧 · 漫画 · 轻小说 · Cosplay</title>
      <meta name="description" content="91次元是专为二次元爱好者打造的内容聚合平台，汇聚最新动漫番剧资讯、热门漫画推荐、精品轻小说阅读和超燃Cosplay作品。在91次元发现同好、探索无限次元世界、开启你的次元之旅。" />
      <meta name="keywords" content="91次元,91次元动漫,91次元二次元社区,91次元番剧推荐,91次元Cosplay,91次元轻小说,91次元同好交流" />
      <link rel="canonical" href="https://91acgn.com/" />
      <meta property="og:title" content="91次元 - 二次元爱好者的次元聚集地" />
      <meta property="og:description" content="汇聚最新动漫番剧资讯、热门漫画推荐、精品轻小说阅读和超燃Cosplay作品。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://91acgn.com/" />
      <meta property="og:site_name" content="91次元" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="91次元 - 二次元爱好者的次元聚集地" />
      <meta name="twitter:description" content="汇聚最新动漫番剧资讯、热门漫画推荐、精品轻小说阅读和超燃Cosplay作品。" />
      <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>

      <main>
        <HeroSection />

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-200 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-xl text-foreground-600 group-hover:text-primary-500 transition-colors`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">精选推荐</h2>
              <Link to="/category/anime" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden hover:ring-2 hover:ring-primary-200 transition-all cursor-pointer"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400" />
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full whitespace-nowrap">
                      {article.categoryName}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-foreground-900 line-clamp-2 mb-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.date} · {article.readTime}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">最新资讯</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex gap-4 p-4 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="w-24 h-24 flex-shrink-0 rounded-md overflow-hidden">
                    <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-primary-500 font-medium whitespace-nowrap">{article.categoryName}</span>
                    <h3 className="text-sm font-medium text-foreground-900 mt-1 mb-1.5 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-thumb-up-line"></i> {article.likes}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Cosplay Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">Cosplay 精选</h2>
              <Link to="/category/cosplay" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1">
                更多作品 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {cosplayArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden hover:ring-2 hover:ring-accent-200 transition-all cursor-pointer"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">{article.title}</h3>
                    <p className="text-sm text-foreground-500 line-clamp-2">{article.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Light Novel Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">轻小说推荐</h2>
              <Link to="/category/light-novel" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1">
                更多推荐 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {novelArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex gap-4 p-5 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden">
                    <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-3 leading-snug group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mt-2">
                      <span>{article.author}</span>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 md:py-16 bg-primary-500">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-sm text-white/70">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              加入 91次元，开启你的次元之旅
            </h2>
            <p className="text-foreground-600 max-w-[600px] mx-auto mb-8 leading-relaxed">
              在这里，遇见同频的二次元伙伴，发现你热爱的每一部番剧、每一本漫画、每一个动人的故事。
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/about"
                className="px-8 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
              <Link
                to="/category/anime"
                className="px-8 py-3 bg-background-200 text-foreground-800 text-sm font-medium rounded-full hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                开始探索
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}